/**
 * Quote Engine — 0 API calls, 0 tokens.
 *
 * Position in the pipeline (main.ts):
 *
 *   PDF upload → browser analysis → resolveTranslationScope() → selected units
 *   → isProviderBoundUnit() filter (translate/batch.ts, the same gate the
 *     translation pipeline applies)                               ← billable units
 *   → buildQuote()                                                 ← here
 *   → 顯示價格 → 開始翻譯 → [future: PAYUNi] → terminology → translation → QA
 *
 * This module only READS the final logical units the analysis produced. It
 * never touches the PDF pipeline, never mutates a unit, has no client and no
 * network access, and never stores PDF text: a QuoteSnapshot holds counts,
 * hashes and money only.
 *
 * Price rule (spec §9–§12):
 *   tierPrice               from PRICING_TIERS on billableChars
 *   minimumSustainablePrice smallest price whose variable margin, after the
 *                           safety-factored API cost and the payment fee,
 *                           reaches TARGET_MIN_MARGIN_RATE
 *   suggestedPrice          roundUp(max(tierPrice, minimumSustainablePrice, MIN_PRICE_TWD), PRICE_ROUND_STEP)
 */

import { roleOf } from '../pdf/roles';
import type { TranslationBlock } from '../pdf/types';
import type { TranslationScope } from '../scope/scope';
import { isProviderBoundUnit } from '../translate/batch';
import { documentFingerprint, fnv1a } from '../translate/terminology';
import { DEFAULT_PRICING_SETTINGS, type PricingSettings } from './config';

/** Counts per unit kind over the billable units (statistics only; they do not set the price). */
export interface UnitBreakdown {
  bodyUnits: number;
  headingUnits: number;
  captionUnits: number;
  footnoteUnits: number;
  tableCellUnits: number;
  figureUnits: number;
  structuredLabelUnits: number;
  sidebarUnits: number;
  otherUnits: number;
}

/** What the scope hands the Quote Engine: the selected units, already resolved. */
export interface QuoteInput {
  /** Selected final logical units (ScopeResolution.units), in document order. */
  units: readonly TranslationBlock[];
  /** Every unit of the document, for the document fingerprint (never stored as text). */
  documentUnits: readonly TranslationBlock[];
  scope: TranslationScope;
  scopeFingerprint: string;
  /** Pages the selected units touch (ScopeResolution.stats.selectedPages). */
  selectedPages: readonly number[];
  /** Pages of the PDF: statistics only. */
  pageCount: number;
}

export interface BillableSummary extends UnitBreakdown {
  billableChars: number;
  billableUnits: number;
  /** Selected units that are not billable (translationEligible=false, numeric-only, empty…). */
  excludedUnits: number;
  /** Ids of the billable units, so the run can be compared with what was really sent. */
  billableUnitIds: string[];
}

export interface EstimatedUsage {
  estimatedInputTokens: number;
  estimatedOutputTokens: number;
  /** input + output (mirrors JobUsage.totalProcessedTokens). */
  estimatedProcessedTokens: number;
  /** Detail for Developer Mode. */
  translationInputTokens: number;
  translationOutputTokens: number;
  terminologyInputTokens: number;
  terminologyOutputTokens: number;
  qaInputTokens: number;
  qaOutputTokens: number;
  estimatedBatches: number;
}

export interface QuoteSnapshot extends BillableSummary, EstimatedUsage {
  quoteVersion: string;
  pricingVersion: string;
  quoteId: string;
  /** ISO timestamps. */
  timestamp: string;
  expiresAt: string;
  documentFingerprint: string;
  scopeFingerprint: string;
  scopeMode: TranslationScope['mode'];
  selectedPages: number[];
  pageCount: number;

  estimatedApiCostUsd: number;
  estimatedApiCostTwd: number;
  safeEstimatedApiCostTwd: number;
  /** Payment fee reserve for the suggested price. */
  estimatedPaymentFee: number;
  /** safeEstimatedApiCostTwd + estimatedPaymentFee. */
  minimumVariableCost: number;

  tierPrice: number;
  minimumSustainablePrice: number;
  suggestedPrice: number;
  /** The amount PAYUNi will be asked for; equals suggestedPrice (spec §30). */
  quotedAmount: number;
}

// ---------------------------------------------------------------------------
// Billable units
// ---------------------------------------------------------------------------

function emptyBreakdown(): UnitBreakdown {
  return {
    bodyUnits: 0,
    headingUnits: 0,
    captionUnits: 0,
    footnoteUnits: 0,
    tableCellUnits: 0,
    figureUnits: 0,
    structuredLabelUnits: 0,
    sidebarUnits: 0,
    otherUnits: 0,
  };
}

function countUnit(b: UnitBreakdown, unit: TranslationBlock): void {
  const role = roleOf(unit);
  if (role === 'STRUCTURED_LABEL') {
    b.structuredLabelUnits++;
    return;
  }
  if (role === 'SIDEBAR' || role === 'CALLOUT_BOX' || role === 'SIDEBAR_HEADING' || role === 'SIDEBAR_LABEL' || role === 'SIDEBAR_BODY') {
    b.sidebarUnits++;
    return;
  }
  switch (unit.type) {
    case 'BODY':
      b.bodyUnits++;
      break;
    case 'HEADING':
    case 'TITLE':
      b.headingUnits++;
      break;
    case 'CAPTION':
      b.captionUnits++;
      break;
    case 'FOOTNOTE':
      b.footnoteUnits++;
      break;
    case 'TABLE':
      b.tableCellUnits++;
      break;
    case 'FIGURE':
      b.figureUnits++;
      break;
    default:
      b.otherUnits++;
  }
}

/**
 * The billable units of a selection: exactly the units translate/batch.ts
 * will send (isProviderBoundUnit). References never reach this list because
 * classify.ts never turns them into units; numeric-only cells, DOIs, URLs,
 * empty texts and decorative rotated strips are refused by the gate.
 */
export function summarizeBillable(units: readonly TranslationBlock[]): BillableSummary {
  const breakdown = emptyBreakdown();
  let chars = 0;
  const ids: string[] = [];
  let excluded = 0;
  for (const unit of units) {
    if (!isProviderBoundUnit(unit)) {
      excluded++;
      continue;
    }
    chars += unit.text.length;
    ids.push(unit.id);
    countUnit(breakdown, unit);
  }
  return { ...breakdown, billableChars: chars, billableUnits: ids.length, excludedUnits: excluded, billableUnitIds: ids };
}

// ---------------------------------------------------------------------------
// Estimated provider usage (heuristic, configurable, no API)
// ---------------------------------------------------------------------------

export function estimateUsage(billableChars: number, billableUnits: number, settings: PricingSettings = DEFAULT_PRICING_SETTINGS): EstimatedUsage {
  const t = settings.tokens;
  if (billableUnits === 0 || billableChars === 0) {
    return {
      estimatedInputTokens: 0,
      estimatedOutputTokens: 0,
      estimatedProcessedTokens: 0,
      translationInputTokens: 0,
      translationOutputTokens: 0,
      terminologyInputTokens: 0,
      terminologyOutputTokens: 0,
      qaInputTokens: 0,
      qaOutputTokens: 0,
      estimatedBatches: 0,
    };
  }
  const batches = Math.max(Math.ceil(billableUnits / t.maxBlocksPerBatch), Math.ceil(billableChars / t.maxCharsPerBatch), 1);
  const translationInput = Math.ceil(billableChars * t.inputTokenFactor + billableUnits * t.unitInputOverhead + batches * t.promptTokensPerBatch);
  const translationOutput = Math.ceil(billableChars * t.outputTokenFactor + billableUnits * t.unitOutputOverhead);
  const terminologyInput = t.terminologyReserve.inputTokens;
  const terminologyOutput = t.terminologyReserve.outputTokens;
  const qaInput = Math.ceil(translationInput * t.qaReserve.inputShare);
  const qaOutput = Math.ceil(translationOutput * t.qaReserve.outputShare);
  const input = translationInput + terminologyInput + qaInput;
  const output = translationOutput + terminologyOutput + qaOutput;
  return {
    estimatedInputTokens: input,
    estimatedOutputTokens: output,
    estimatedProcessedTokens: input + output,
    translationInputTokens: translationInput,
    translationOutputTokens: translationOutput,
    terminologyInputTokens: terminologyInput,
    terminologyOutputTokens: terminologyOutput,
    qaInputTokens: qaInput,
    qaOutputTokens: qaOutput,
    estimatedBatches: batches,
  };
}

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

/** Round to cents; every TWD/USD amount stored in a snapshot goes through this. */
export function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Round UP to the next cent (a safety price never rounds down). */
export function ceilMoney(n: number): number {
  return Math.ceil(n * 100 - 1e-9) / 100;
}

/**
 * Provider cost from token counts. `cachedInputTokens ⊆ inputTokens` and
 * `reasoningTokens ⊆ outputTokens`, so the non-cached share is input − cached
 * and reasoning is never added on top (it is already inside output).
 */
export function apiCostUsd(
  usage: { inputTokens: number; cachedInputTokens?: number; outputTokens: number },
  api: PricingSettings['api'] = DEFAULT_PRICING_SETTINGS.api,
): number {
  const cached = Math.min(Math.max(0, usage.cachedInputTokens ?? 0), Math.max(0, usage.inputTokens));
  const nonCached = Math.max(0, usage.inputTokens) - cached;
  const usd = (nonCached * api.inputPerMillionUsd + cached * api.cachedInputPerMillionUsd + Math.max(0, usage.outputTokens) * api.outputPerMillionUsd) / 1_000_000;
  return usd;
}

export function usdToTwd(usd: number, api: PricingSettings['api'] = DEFAULT_PRICING_SETTINGS.api): number {
  return usd * api.usdToTwd;
}

/** Payment fee reserve for a given price: max(price × rate, minimum), 0 for a free run. */
export function paymentFee(priceTwd: number, settings: PricingSettings = DEFAULT_PRICING_SETTINGS): number {
  if (priceTwd <= 0) return 0;
  return roundMoney(Math.max(priceTwd * settings.paymentPercentRate, settings.paymentMinFeeTwd));
}

/** Round UP to the next multiple of `step` (21.2 → 25, 25 → 25, 27.8 → 30). */
export function roundUpToStep(price: number, step: number): number {
  if (step <= 0) return Math.ceil(price);
  // 1e-6 absorbs float noise (30.0000001 stays 30); a real cent above the step still rounds up.
  return Math.ceil(price / step - 1e-6) * step || 0;
}

/** v1 tier lookup on billable characters; beyond the last tier the extension steps apply. */
export function tierPrice(billableChars: number, settings: PricingSettings = DEFAULT_PRICING_SETTINGS): number {
  const tiers = settings.tiers;
  if (tiers.length === 0) return settings.minPriceTwd;
  for (const tier of tiers) if (billableChars <= tier.upTo) return tier.priceTwd;
  const last = tiers[tiers.length - 1];
  const extra = billableChars - last.upTo;
  const steps = Math.ceil(extra / settings.tierExtension.charsPerStep);
  return last.priceTwd + steps * settings.tierExtension.pricePerStep;
}

/**
 * Smallest price p with p − safeApiCost − fee(p) ≥ margin × p, where
 * fee(p) = max(minFee, rate × p). With m = margin, r = rate:
 *   p ≥ (safeCost + minFee) / (1 − m)   when the minimum fee applies
 *   p ≥ safeCost / (1 − m − r)          when the percentage applies
 * Both must hold, so the answer is the larger one.
 */
export function minimumSustainablePrice(safeApiCostTwd: number, settings: PricingSettings = DEFAULT_PRICING_SETTINGS): number {
  const m = settings.targetMinMarginRate;
  const r = settings.paymentPercentRate;
  const denomMin = 1 - m;
  const denomRate = 1 - m - r;
  if (denomMin <= 0 || denomRate <= 0) throw new Error('pricing: margin + payment rate must stay below 100%');
  const byMinFee = (safeApiCostTwd + settings.paymentMinFeeTwd) / denomMin;
  const byRate = safeApiCostTwd / denomRate;
  return ceilMoney(Math.max(byMinFee, byRate));
}

/** Final price rule: max(tier, sustainable, minimum), rounded up to the step. */
export function suggestedPrice(tier: number, sustainable: number, settings: PricingSettings = DEFAULT_PRICING_SETTINGS): number {
  const raw = Math.max(tier, sustainable, settings.minPriceTwd);
  return Math.max(roundUpToStep(raw, settings.priceRoundStep), settings.minPriceTwd);
}

// ---------------------------------------------------------------------------
// Quote
// ---------------------------------------------------------------------------

function newQuoteId(now: number): string {
  const rnd =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${fnv1a(String(now))}-${fnv1a(String(Math.random()))}-${fnv1a(String(Math.random()))}`;
  return `q_${rnd}`;
}

export interface BuildQuoteOptions {
  settings?: PricingSettings;
  /** Clock override for tests (ms since epoch). */
  now?: number;
}

/**
 * Build the QuoteSnapshot for a resolved scope. Pure apart from the clock and
 * the quote id; makes no request and keeps no text.
 */
export function buildQuote(input: QuoteInput, options: BuildQuoteOptions = {}): QuoteSnapshot {
  const settings = options.settings ?? DEFAULT_PRICING_SETTINGS;
  const now = options.now ?? Date.now();
  const billable = summarizeBillable(input.units);
  const usage = estimateUsage(billable.billableChars, billable.billableUnits, settings);

  const costUsd = apiCostUsd({ inputTokens: usage.estimatedInputTokens, cachedInputTokens: 0, outputTokens: usage.estimatedOutputTokens }, settings.api);
  const costTwd = usdToTwd(costUsd, settings.api);
  const safeTwd = costTwd * settings.safetyFactor;

  const hasContent = billable.billableUnits > 0;
  const tier = hasContent ? tierPrice(billable.billableChars, settings) : 0;
  const sustainable = hasContent ? minimumSustainablePrice(safeTwd, settings) : 0;
  const price = hasContent ? suggestedPrice(tier, sustainable, settings) : 0;
  const fee = paymentFee(price, settings);

  return {
    ...billable,
    ...usage,
    quoteVersion: settings.pricingVersion,
    pricingVersion: settings.pricingVersion,
    quoteId: newQuoteId(now),
    timestamp: new Date(now).toISOString(),
    expiresAt: new Date(now + settings.quoteTtlMs).toISOString(),
    documentFingerprint: documentFingerprint(input.documentUnits),
    scopeFingerprint: input.scopeFingerprint,
    scopeMode: input.scope.mode,
    selectedPages: [...input.selectedPages],
    pageCount: input.pageCount,
    estimatedApiCostUsd: Math.round(costUsd * 10_000) / 10_000,
    estimatedApiCostTwd: roundMoney(costTwd),
    safeEstimatedApiCostTwd: roundMoney(safeTwd),
    estimatedPaymentFee: fee,
    minimumVariableCost: roundMoney(safeTwd + fee),
    tierPrice: tier,
    minimumSustainablePrice: sustainable,
    suggestedPrice: price,
    quotedAmount: price,
  };
}

/** True while the quote may still be used (PAYUNi will re-check server-side). */
export function isQuoteValid(quote: QuoteSnapshot, now = Date.now()): boolean {
  return Date.parse(quote.expiresAt) > now;
}

/** Keys of a snapshot that could ever carry text — none; kept as a guard for the "no PDF text" tests. */
export const QUOTE_TEXT_FREE_KEYS: readonly (keyof QuoteSnapshot)[] = [
  'quoteId',
  'timestamp',
  'expiresAt',
  'documentFingerprint',
  'scopeFingerprint',
  'scopeMode',
  'quoteVersion',
  'pricingVersion',
];
